import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GavelIcon from '@mui/icons-material/Gavel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BENEFITS, FAQ } from '../../constants/faq';
import { Link } from 'react-router-dom';

function FAQItem({ question, answer }: { question: string; answer: string[] }) {
	const [isOpen, setIsOpen] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const maxHeight = isOpen && contentRef.current ? `${contentRef.current.scrollHeight}px` : '0';

	return (
		<div className={clsx(
			'rounded-2xl overflow-hidden transition-all duration-300 border',
			isOpen ? 'border-primary-200 shadow-soft-lg' : 'border-gray-100 shadow-soft bg-white hover:border-primary-100'
		)}>
			<button
				className={clsx(
					'w-full text-left p-6 flex items-center justify-between gap-4 transition-all duration-300',
					isOpen ? 'bg-primary-50' : 'bg-white hover:bg-gray-50/80'
				)}
				onClick={() => setIsOpen(prev => !prev)}
			>
				<span className={clsx(
					'font-montserrat-custom-medium font-bold text-left',
					isOpen ? 'text-primary-700' : 'text-gray-800'
				)}>
					{question}
				</span>
				<div className={clsx(
					'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300',
					isOpen ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
				)}>
					<ExpandMoreIcon
						fontSize="small"
						className={clsx('transition-transform duration-300', isOpen ? 'rotate-180' : '')}
					/>
				</div>
			</button>
			<div
				ref={contentRef}
				style={{ maxHeight, overflow: 'hidden', transition: 'max-height 0.35s ease-in-out' }}
			>
				<div className="px-6 pb-6 pt-4 bg-primary-50/40 border-t border-primary-100">
					<ul className="space-y-3">
						{answer.map((value, index) => (
							<li key={index} className="flex items-start gap-3">
								<CheckCircleIcon
									className="text-primary-400 mt-0.5 flex-shrink-0"
									fontSize="small"
								/>
								<span className="font-montserrat-custom-medium text-gray-600">
									{value}
								</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}

function RegulaminSection() {
	const [isOpen, setIsOpen] = useState(false);
	const contentRef = useRef<HTMLDivElement>(null);
	const maxHeight = isOpen && contentRef.current ? `${contentRef.current.scrollHeight}px` : '0';

	const paragraphs = [
		{
			title: '§1. Postanowienia ogólne',
			items: [
				'Treningi personalne oraz zajęcia grupowe prowadzone są przez Trenerkę Amelię Pałczyńską w Studiu Sportu Dual przy Alei Wincentego Witosa 11 A w Rzeszowie.',
				'Każdy uczestnik zajęć zobowiązany jest do zapoznania się z niniejszym regulaminem i jego przestrzegania.',
			],
		},
		{
			title: '§2. Warunki uczestnictwa',
			items: [
				'Warunkiem uczestnictwa jest złożenie wypełnionych i podpisanych dokumentów: Oświadczenia uczestnika oraz Klauzuli informacyjnej RODO.',
				'Osoby poniżej 18. roku życia wymagają zgody rodzica lub opiekuna prawnego.',
			],
		},
		{
			title: '§3. Opłaty',
			items: [
				'Uczestnik zobowiązany jest do uiszczenia opłaty z góry, zgodnie z obowiązującym cennikiem.',
				'Opłata powinna zostać dokonana najpóźniej w dniu rozpoczęcia zajęć, przed ich rozpoczęciem.',
			],
		},
		{
			title: '§4. Rezygnacja i nieobecność',
			items: [
				'Uczestnikowi nie przysługuje zwrot opłaty w przypadku nieobecności na umówionym treningu lub rezygnacji z dalszego uczestnictwa.',
				'Zmiana terminu zajęć jest możliwa wyłącznie za zgodą Organizatora i po wcześniejszym uzgodnieniu nowego terminu.',
			],
		},
		{
			title: '§5. Odwołanie zajęć przez Organizatora',
			items: [
				'W przypadku odwołania zajęć przez Organizatora, zostaną one przeprowadzone w innym, wspólnie ustalonym terminie bez dodatkowych opłat.',
			],
		},
		{
			title: '§6. Bezpieczeństwo i stan zdrowia',
			items: [
				'Do udziału dopuszczone są wyłącznie osoby, których stan psychofizyczny pozwala na bezpieczne uczestnictwo w aktywności fizycznej.',
				'Zabrania się udziału osobom będącym pod wpływem alkoholu, narkotyków lub innych substancji.',
				'Uczestnik zobowiązany jest do poinformowania Organizatora o wszelkich dolegliwościach zdrowotnych.',
			],
		},
	];

	return (
		<div className={clsx(
			'rounded-2xl overflow-hidden transition-all duration-300 border',
			isOpen ? 'border-primary-200 shadow-soft-lg' : 'border-gray-100 shadow-soft bg-white hover:border-primary-100'
		)}>
			<button
				className={clsx(
					'w-full text-left p-6 flex items-center justify-between gap-4 transition-all duration-300',
					isOpen ? 'bg-primary-50' : 'bg-white hover:bg-gray-50/80'
				)}
				onClick={() => setIsOpen(prev => !prev)}
			>
				<div className="flex items-center gap-3">
					<GavelIcon fontSize="small" className={isOpen ? 'text-primary-600' : 'text-gray-400'} />
					<span className={clsx(
						'font-montserrat-custom-medium font-bold text-left',
						isOpen ? 'text-primary-700' : 'text-gray-800'
					)}>
						Regulamin współpracy
					</span>
				</div>
				<div className={clsx(
					'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300',
					isOpen ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
				)}>
					<ExpandMoreIcon
						fontSize="small"
						className={clsx('transition-transform duration-300', isOpen ? 'rotate-180' : '')}
					/>
				</div>
			</button>
			<div
				ref={contentRef}
				style={{ maxHeight, overflow: 'hidden', transition: 'max-height 0.35s ease-in-out' }}
			>
				<div className="px-6 pb-8 pt-4 bg-primary-50/40 border-t border-primary-100">
					<p className="text-sm text-gray-500 font-montserrat-custom-medium mb-6">
						Regulamin treningów personalnych i zajęć rekreacyjno-sportowych obowiązuje od dnia podpisania.
					</p>
					<div className="space-y-5">
						{paragraphs.map((para, i) => (
							<div key={i}>
								<h4 className="font-bold text-gray-800 text-sm mb-2 font-montserrat-custom-medium">{para.title}</h4>
								<ul className="space-y-2">
									{para.items.map((item, j) => (
										<li key={j} className="flex items-start gap-3">
											<CheckCircleIcon className="text-primary-400 mt-0.5 flex-shrink-0" fontSize="small" />
											<span className="font-montserrat-custom-medium text-gray-600 text-sm">{item}</span>
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
					<p className="text-xs text-gray-400 font-montserrat-custom-medium mt-6">
						Udział w zajęciach jest równoznaczny z akceptacją niniejszego regulaminu. W sprawach nieuregulowanych zastosowanie mają przepisy prawa polskiego.
					</p>
				</div>
			</div>
		</div>
	);
}

export default function FAQPage() {
	return (
		<div className="pt-20 lg:pt-24 pb-16">
			<div className="relative overflow-hidden bg-gradient-to-br from-[#fdf5f4] via-white to-[#faf6f2] py-16 lg:py-20 mb-16">
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute -top-16 -right-16 w-80 h-80 bg-primary-100/60 rounded-full blur-3xl" />
					<div className="absolute bottom-0 left-10 w-64 h-64 bg-[#f3ebe1]/60 rounded-full blur-3xl" />
				</div>
				<div className="relative max-w-4xl mx-auto px-6 text-center">
					<span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-xs font-bold mb-5 tracking-widest uppercase font-montserrat-custom-medium">
						WSPÓŁPRACA
					</span>
					<h1 className="font-bold-custom text-5xl lg:text-6xl text-gray-900 mb-4">
						Co <span className="text-gradient">otrzymujesz?</span>
					</h1>
					<p className="text-gray-500 font-montserrat-custom-medium text-lg max-w-xl mx-auto">
						Sprawdź co wchodzi w skład naszej współpracy i odpowiedzi na najczęstsze pytania.
					</p>
				</div>
			</div>

			<div className="max-w-4xl mx-auto px-4 sm:px-6">
				<div className="relative bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl p-8 lg:p-12 mb-16 overflow-hidden">
					<div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
					<div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
					<div className="relative">
						<h3 className="font-bold-custom text-2xl lg:text-3xl text-white mb-2 text-center">
							Podejmując współpracę ze mną otrzymujesz:
						</h3>
						<p className="text-primary-100 text-center font-montserrat-custom-medium mb-10">
							Kompleksowe wsparcie na każdym etapie
						</p>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							{BENEFITS.map(({ benefitText }, index) => (
								<div
									key={index}
									className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/15"
								>
									<div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white flex-shrink-0">
										<CheckCircleIcon fontSize="small" />
									</div>
									<span className="font-montserrat-custom-medium text-white/90 text-sm">
										{benefitText}
									</span>
								</div>
							))}
						</div>
						<div className="mt-10 text-center">
							<Link
								to="/contact"
								className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 font-bold rounded-full shadow-soft-lg hover:scale-105 transition-all duration-300"
							>
								ZAPISZ SIĘ NA TRENING
								<ArrowForwardIcon fontSize="small" />
							</Link>
						</div>
					</div>
				</div>

				<div className="mb-16">
					<div className="text-center mb-10">
						<h2 className="font-bold-custom text-3xl lg:text-4xl text-gray-900">
							Najczęściej zadawane <span className="text-gradient">pytania</span>
						</h2>
					</div>
					<div className="space-y-3">
						{FAQ.map(({ question, answers }, index) => (
							<FAQItem
								key={index}
								question={question}
								answer={answers}
							/>
						))}
						<RegulaminSection />
					</div>
				</div>
			</div>
		</div>
	);
}
